import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AcShieldLogo } from './AcShieldLogo';

// Delays absolutos (ms desde o mount) — animações CSS one-shot, não reiniciam
const SHIELD_DELAY = 80;
const WORD1_START = 200;
const WORD2_START = 750;
const SUBTITLE_DELAY = 1500;
const EXIT_DELAY = 3000;  // Reduzido para não ficar parada esperando (era 4500)
const DONE_DELAY = 3800;  // Reduzido na mesma proporção (era 5300)
const LETTER_GAP_W1 = 80;
const LETTER_GAP_W2 = 58;

// SVG paths (lucide-style, viewBox 24x24)
const P_CAP = <><polygon points="12 2 22 8.5 12 15 2 8.5" /><path d="M7 11v5c2.5 2.5 7.5 2.5 10 0v-5" /><line x1="22" y1="8.5" x2="22" y2="13" /></>;
const P_BOOK = <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>;
const P_STAR = <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />;
const P_PENCIL = <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />;
const P_AWARD = <><circle cx="12" cy="8" r="6" /><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" /></>;

// Ícones ambiente educacional espalhados no fundo
const AMBIENT_ICON_SEEDS = [
  [7, 12, 104, 46, -28, 0.08, 18, 0],
  [24, 62, 132, -52, 38, 0.10, 22, 3],
  [45, 18, 92, 34, 44, 0.07, 20, 6],
  [58, 82, 148, -44, -34, 0.09, 24, 1],
  [78, 36, 118, 52, 28, 0.08, 19, 4],
  [92, 72, 156, -38, 46, 0.07, 26, 8],
  [15, 88, 86, 42, -36, 0.10, 21, 5],
  [34, 42, 120, -48, 32, 0.06, 18, 2],
  [66, 54, 104, 40, -40, 0.09, 23, 7],
  [84, 16, 78, -30, 30, 0.08, 17, 1],
  [5, 70, 144, 48, 42, 0.07, 25, 4],
  [52, 6, 92, -42, 34, 0.06, 20, 9],
  [72, 92, 124, 36, -46, 0.08, 24, 3],
  [96, 28, 102, -48, -24, 0.09, 19, 6],
  [41, 96, 162, 54, -38, 0.06, 28, 10],
] as const;

const AMBIENT_ICONS = AMBIENT_ICON_SEEDS.map((seed, i) => ({
  x: seed[0],
  y: seed[1],
  size: seed[2],
  dx: seed[3],
  dy: seed[4],
  opacity: seed[5],
  dur: seed[6],
  fadeDur: Math.max(8, seed[6] - 6),
  delay: seed[7],
  anim: ['float-a', 'float-b', 'float-c'][i % 3],
  color: ['#c4b5fd', '#a78bfa', '#93c5fd', '#ddd6fe'][i % 4],
  path: [P_CAP, P_BOOK, P_STAR, P_PENCIL, P_AWARD][i % 5]
}));


// Componente fora do pai — não remonta quando pai re-renderiza
const AnimLetter: React.FC<{ char: string; delay: number; color: string; fontSize: string }> = (
  { char, delay, color, fontSize }
) => (
  <span style={{
    display: 'inline-block',
    fontSize, fontWeight: 900, color,
    letterSpacing: '-0.02em', lineHeight: 1,
    animation: `ltr-pop 0.5s cubic-bezier(0.34, 1.50, 0.64, 1) ${delay}ms both`,
  }}>
    {char === ' ' ? '\u00A0' : char}
  </span>
);

interface Props {
  titleWord1?: string;
  titleWord2?: string;
  subtitle?: string;
  settledScale?: number;
  onComplete?: () => void;
  children?: React.ReactNode;
  brandFooter?: React.ReactNode;
  skipIntro?: boolean;
  alignLogoRight?: boolean;
  splitPct?: number;
  hideBackButton?: boolean;
  /** Se informado, substitui o navigate('/') do botão voltar (ex.: limpeza de sessão no simulador). */
  onBackClick?: () => void;
}

export const SovereignSplash: React.FC<Props> = ({
  titleWord1 = 'Aluno',
  titleWord2 = 'Conectado',
  subtitle = 'Plataforma Acesso a Educacao',
  settledScale = 1,
  onComplete,
  children,
  brandFooter,
  skipIntro = false,
  alignLogoRight = false,
  splitPct = 42,
  hideBackButton = false,
  onBackClick,
}) => {
  const navigate = useNavigate();
  const [isExit, setIsExit] = useState(skipIntro);
  const [isDone, setIsDone] = useState(skipIntro);
  const [isPwa, setIsPwa] = useState(false);
  const onCompleteRef = React.useRef<Props['onComplete']>(onComplete);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone
      || document.referrer.includes('android-app://');
    setIsPwa(isStandalone);
  }, []);

  useEffect(() => {
    const origHtmlOverflow = document.documentElement.style.overflow;
    const origBodyOverflow = document.body.style.overflow;
    const origBodyPosition = document.body.style.position;
    const origBodyWidth = document.body.style.width;
    const origBodyHeight = document.body.style.height;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      document.documentElement.style.overflow = origHtmlOverflow;
      document.body.style.overflow = origBodyOverflow;
      document.body.style.position = origBodyPosition;
      document.body.style.width = origBodyWidth;
      document.body.style.height = origBodyHeight;
    };
  }, []);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (skipIntro) {
      setIsExit(true);
      setIsDone(true);
      onCompleteRef.current?.();
      return;
    }
    // Se mudar para false (replay), resetamos os estados para iniciar do zero
    setIsExit(false);
    setIsDone(false);

    const t1 = setTimeout(() => setIsExit(true), EXIT_DELAY);
    const t2 = setTimeout(() => { setIsDone(true); onCompleteRef.current?.(); }, DONE_DELAY);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [skipIntro]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [theme, setTheme] = useState<'purple' | 'dark' | 'light'>(() => {
    try {
      return (localStorage.getItem('ac_desktop_theme') as any) || 'purple';
    } catch {
      return 'purple';
    }
  });

  useEffect(() => {
    const handleThemeChange = () => {
      try {
        setTheme((localStorage.getItem('ac_desktop_theme') as any) || 'purple');
      } catch {}
    };
    window.addEventListener('ac_theme_changed', handleThemeChange);
    return () => window.removeEventListener('ac_theme_changed', handleThemeChange);
  }, []);

  // Tamanhos que transitam suavemente quando o exit acontece
  const desktopSettledScale = !isMobile && isExit ? Math.min(Math.max(settledScale, 1), 1.2) : 1;
  const shieldPx = isExit ? (isMobile ? 72 : Math.round(176 * desktopSettledScale)) : (isMobile ? 160 : 260);
  const titleRem = isExit ? (isMobile ? 1.65 : 3.5 * desktopSettledScale) : undefined;
  const subtitleRem = isExit ? (isMobile ? 0.92 : 1.55 * Math.min(desktopSettledScale, 1.14)) : undefined;
  const underlineWidth = isMobile ? 180 : Math.round(250 * desktopSettledScale);

  const ambientBackgrounds = {
    purple: 'linear-gradient(160deg, #19072d 0%, #3b1682 52%, #8b5cf6 100%)',
    dark: 'linear-gradient(160deg, #09090b 0%, #18181b 52%, #27272a 100%)',
    light: 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 52%, #cbd5e1 100%)',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: ambientBackgrounds[theme],
      backgroundSize: '100% 100%',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      color: theme === 'light' ? '#1e293b' : '#cdd6f4',
      scrollbarWidth: 'none' as any,
    }}>

      {/* ── Ícones educacionais ambiente — sempre visíveis, sutis ── */}
      {AMBIENT_ICONS.map((item, i) => (
        <svg key={`amb-${i}`}
          viewBox="0 0 24 24" fill="none"
          stroke={item.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{
            position: 'absolute',
            left: `${item.x}%`, top: `${item.y}%`,
            width: item.size, height: item.size,
            opacity: 0, // Animado pelos keyframes de float
            pointerEvents: 'none',
            zIndex: 1,
            ['--ac-drift-x' as any]: `${item.dx}px`,
            ['--ac-drift-y' as any]: `${item.dy}px`,
            ['--ac-icon-opacity' as any]: item.opacity,
            animation: `${item.anim} ${item.dur}s ease-in-out ${item.delay}s infinite, icon-fade ${item.fadeDur}s ease-in-out ${item.delay}s infinite`,
          }}
        >
          {item.path}
        </svg>
      ))}

      {/* ── Partículas ── */}
      {!isExit && [...Array(8)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute', left: '50%', top: '44%',
          width: 4, height: 4, borderRadius: '50%',
          background: i % 3 === 0 ? '#c4b5fd' : '#a78bfa',
          boxShadow: '0 0 10px 3px rgba(167,139,250,0.5)',
          pointerEvents: 'none',
          animation: `particle-out 1s ease ${SHIELD_DELAY + 1800 + i * 100}ms both`,
        }} />
      ))}

      {/* ── Glow radial ── */}
      {!isExit && (
        <div style={{
          position: 'absolute', left: '50%', top: '44%',
          width: 480, height: 480, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)',
          filter: 'blur(55px)',
          transform: 'translate(-50%, -50%)',
          animation: `glow-in 1.8s ease ${SHIELD_DELAY + 200}ms both, glow-pulse 2.8s ease-in-out ${SHIELD_DELAY + 2000}ms infinite`,
          pointerEvents: 'none',
        }} />
      )}

      {/* ── ÚNICO bloco de marca — move-se suavemente para o destino (centro → topo/esquerda) ── */}
      <div style={{
        position: 'absolute',
        // Desktop: Lado esquerdo centralizado | Mobile: Topo centralizado
        left: isExit ? (!isMobile ? (alignLogoRight ? '75%' : `${splitPct / 2}%`) : '50%') : '50%',
        top: isExit ? (!isMobile ? '50%' : (isDone ? '0' : '6%')) : '44%',
        transform: isExit ? (!isMobile ? (alignLogoRight ? 'translate(-50%, -50%) scale(clamp(0.65, 0.8vw + 0.35, 1.35))' : 'translate(-50%, -50%)') : 'translate(-50%, 0)') : 'translate(-50%, -50%)',
        transition: 'all 0.95s cubic-bezier(0.34, 1.10, 0.64, 1)',
        width: !isMobile && isExit ? (alignLogoRight ? '55%' : `${splitPct + 2}%`) : '100%',
        padding: isDone ? (isMobile ? 'clamp(12px, 2.5vh, 24px) clamp(16px, 5vw, 24px) 20px' : '0 32px') : '0 clamp(16px, 5vw, 72px)',
        boxSizing: 'border-box' as any,
        zIndex: isDone && alignLogoRight ? 0 : 10,
        opacity: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        // Backdrop apenas no mobile para não cobrir o conteúdo lateral no desktop
        background: isDone && isMobile ? 'linear-gradient(to bottom, rgba(31, 10, 64, 0.94) 0%, rgba(74, 25, 156, 0.70) 72%, rgba(74, 25, 156, 0.18) 90%, transparent 100%)' : 'transparent',
        backdropFilter: isDone && isMobile ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: isDone && isMobile ? 'blur(20px)' : 'none',
        maskImage: isDone && isMobile ? 'linear-gradient(to bottom, black 75%, transparent 100%)' : 'none',
        WebkitMaskImage: isDone && isMobile ? 'linear-gradient(to bottom, black 75%, transparent 100%)' : 'none',
        pointerEvents: 'none',
      }}>

        {/* Escudo: cresce até 80% da tela e volta suavemente */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          width: `${shieldPx}px`, height: `${shieldPx}px`,
          transition: 'width 0.95s cubic-bezier(0.34, 1.10, 0.64, 1), height 0.95s cubic-bezier(0.34, 1.10, 0.64, 1)',
          animation: `shield-intro 2.1s cubic-bezier(0.25, 1, 0.5, 1) ${SHIELD_DELAY}ms both`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.15, filter: 'blur(30px)', background: '#a78bfa' }} />
          <AcShieldLogo />
        </div>

        {/* Título — letras pull-up, fica lado a lado ou quebra no mobile */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          zIndex: 1,
          gap: isMobile ? 8 : '16px',
          marginTop: isExit ? (isMobile ? '16px' : '12px') : '24px',
          transition: 'margin 0.95s cubic-bezier(0.34, 1.10, 0.64, 1)',
        }}>
          <span style={{ display: 'inline-flex' }}>
            {titleWord1.split('').map((ch, i) => (
              <AnimLetter key={`w1${i}`} char={ch} color="#ffffff"
                fontSize={titleRem ? `${titleRem}rem` : 'clamp(4rem, 12vw, 10rem)'}
                delay={WORD1_START + i * LETTER_GAP_W1} />
            ))}
          </span>
          <span style={{ display: 'inline-flex' }}>
            {titleWord2.split('').map((ch, i) => (
              <AnimLetter key={`w2${i}`} char={ch} color="#a78bfa"
                fontSize={titleRem ? `${titleRem}rem` : 'clamp(4rem, 12vw, 10rem)'}
                delay={WORD2_START + i * LETTER_GAP_W2} />
            ))}
          </span>
        </div>

        {/* ── Subtítulo & Linha ── */}
        <div style={{
          marginTop: isMobile ? (isExit ? '10px' : '0.5rem') : '1rem',
          display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}>
          <div style={{
            fontSize: subtitleRem ? `${subtitleRem}rem` : 'clamp(1.1rem, 2vw, 1.55rem)',
            fontWeight: 500,
            color: '#f8fafc',
            textShadow: '0 0 10px #a78bfa, 0 2px 4px rgba(167, 139, 250, 0.9)',
            letterSpacing: '0.02em',
            animation: `acm-fadeInUp 0.8s cubic-bezier(0.34, 1.50, 0.64, 1) ${SUBTITLE_DELAY}ms both`,
            transition: 'font-size 0.95s cubic-bezier(0.34, 1.10, 0.64, 1)',
          }}>
            {subtitle}
          </div>
          {/* Linha que cresce */}
          <div style={{ marginTop: isMobile ? '12px' : '1.5rem', width: underlineWidth, height: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, transparent, #8b5cf6, #3b82f6, transparent)',
              width: '0%',
              borderRadius: 2,
              animation: `line-grow 1.2s ease ${SUBTITLE_DELAY + 400}ms both`,
            }} />
          </div>
          {isDone && brandFooter && (
            <div style={{
              marginTop: isMobile ? 14 : 18,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'auto',
            }}>
              {brandFooter}
            </div>
          )}
        </div>
      </div>

      {/* ── Botão Voltar ── */}
      {isDone && !isMobile && !isPwa && !hideBackButton && (
        <button
          onClick={() => {
            if (onBackClick) {
              onBackClick();
              return;
            }
            navigate('/');
          }}
          style={{
            position: 'absolute', top: isMobile ? 16 : 24, left: isMobile ? 16 : 24, zIndex: 9999,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%',
            width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', cursor: 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(10px)',
            animation: 'acm-fadeIn 0.5s ease 0.2s both'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.transform = 'scale(1)'; }}
          title="Voltar"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </button>
      )}

      {/* ── Botão Instalar (Removido conforme solicitação) ── */}

      {/* ── Modal Instalação PWA ── */}
      {/* ── Card/Conteúdo — aparece no centro (mobile) ou direita (desktop) ── */}
      {isDone && (
        <div style={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: isMobile ? 0 : (alignLogoRight ? 0 : `${splitPct}%`),
          right: 0,
          zIndex: isDone && alignLogoRight ? 10 : undefined,
          display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-start',
          paddingTop: isMobile ? 'clamp(200px, 24vh, 232px)' : 0,
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0,
          overflow: 'hidden',
          scrollbarWidth: 'none',
          animation: 'card-rise 0.7s cubic-bezier(0.34, 1.45, 0.64, 1) 0.15s both',
        }}>
          <div style={{
            width: '100%', maxWidth: isMobile ? '100%' : (alignLogoRight ? '100%' : 1180),
            margin: isMobile ? '0' : (alignLogoRight ? '0' : '0 auto'),
            display: 'flex', flexDirection: 'column',
            flex: 1,
            height: '100%',
          }}>
            {children}
          </div>
        </div>
      )}

      <style>{`
        /* Oculta scrollbars (funcional mas invísível) */
        ::-webkit-scrollbar { display: none; }
        * { scrollbar-width: none; }

        @keyframes pan-bg {
          0%,100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }

        /* Escudo: aparece do zero, EXPLODE para ~80% da tela, volta ao tamanho normal */
        @keyframes shield-cinematic {
          0%   { opacity: 0;    transform: scale(0.03); filter: blur(10px) brightness(0.1); }
          18%  { opacity: 0.25; transform: scale(0.35); filter: blur(4px)  brightness(0.5); }
          42%  { opacity: 1;    transform: scale(5.4);  filter: blur(8px)  brightness(1.4); }
          58%  { opacity: 1;    transform: scale(1.06); filter: blur(0px)  brightness(1);   }
          72%  { transform: scale(0.96); }
          84%  { transform: scale(1.02); }
          100% { opacity: 1;    transform: scale(1);    filter: none; }
        }

        /* Letras: slide up sem scale — sem distorção de layout */
        @keyframes ltr-pop {
          0%   { opacity: 0; transform: translateY(22px); }
          55%  { opacity: 1; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0);    }
        }

        /* Subtítulo blur-to-clear */
        @keyframes subtitle-reveal {
          0%   { opacity: 0; filter: blur(12px); transform: translateY(10px); }
          100% { opacity: 1; filter: blur(0px);  transform: translateY(0);    }
        }

        /* Linha */
        @keyframes line-grow {
          0%   { width: 0; }
          100% { width: clamp(60px, 11vw, 120px); }
        }

        /* Partículas */
        @keyframes particle-out {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0); }
          20%  { opacity: 0.8; }
          100% {
            opacity: 0;
            transform: translate(
              calc(-50% + var(--px, 0px)),
              calc(-50% + var(--py, 0px))
            ) scale(0);
          }
        }

        /* Glow */
        @keyframes glow-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes glow-pulse {
          0%,100% { transform: translate(-50%,-50%) scale(1);    opacity: 0.75; }
          50%      { transform: translate(-50%,-50%) scale(1.14); opacity: 1;    }
        }

        /* Card */
        @keyframes card-rise {
          0%   { opacity: 0; transform: translateY(50px) scale(0.96); }
          60%  { opacity: 1; transform: translateY(-6px)  scale(1.01); }
          100% { transform: translateY(0) scale(1); }
        }

        /* Ícones ambiente — flutuação suave e pulsação de universo */
        @keyframes float-a {
          0%,100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          35%     { transform: translate3d(calc(var(--ac-drift-x) * 0.55), calc(var(--ac-drift-y) * -0.45), 0) rotate(9deg) scale(1.05); }
          70%     { transform: translate3d(var(--ac-drift-x), var(--ac-drift-y), 0) rotate(-6deg) scale(0.98); }
        }
        @keyframes float-b {
          0%,100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          40%     { transform: translate3d(calc(var(--ac-drift-x) * -0.5), calc(var(--ac-drift-y) * 0.6), 0) rotate(-10deg) scale(1.08); }
          75%     { transform: translate3d(var(--ac-drift-x), calc(var(--ac-drift-y) * -0.8), 0) rotate(6deg) scale(0.96); }
        }
        @keyframes float-c {
          0%,100% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          50%     { transform: translate3d(var(--ac-drift-x), var(--ac-drift-y), 0) rotate(5deg) scale(1.1); }
        }
        @keyframes icon-fade {
          0%,100% { opacity: 0; }
          50%     { opacity: var(--ac-icon-opacity); }
        }
      `}</style>
    </div>
  );
};

export default SovereignSplash;
